import { useMutation } from "@tanstack/react-query";
import { Archive, Loader2, RotateCcw } from "lucide-react";
import React, { useState } from "react";

import {
  createSampleTransfers,
  type InspectionDetail,
  type ItemView,
  type SampleTransferView,
  type StockSourcesView,
} from "@/api/okk_receiving";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDateTime, ruNum } from "@/lib/format";
import { authorName } from "@/lib/okk-receiving";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { StatusBadge } from "@/pages/order-journal/parts";

/**
 * Образцы «в архив» → документ 1С `ПеремещениеЗапасов` склад-источник →
 * склад арбитража (бэкенд `okk_receiving/samples.py`).
 *
 * Склад-источник выбирает ОКК по позиции из складов со свободным остатком;
 * молча не подставляем — даже единственный склад человек выбирает сам.
 */

export const SAMPLES_HINT =
  "Документ ПеремещениеЗапасов в 1С создаётся по одному на склад-источник; нужна проведённая ПН.";

/** Подпись склада в списке «Откуда взяли»: «3 этаж — 18 757 шт». */
export function sourceOptionText(name: string, freeQty: number, unit: string): string {
  return `${name} — ${ruNum.format(freeQty)} ${unit}`;
}

/**
 * «Откуда взяли» у позиции с образцами «в архив» > 0.
 *
 * Список — живьём из 1С (`stock-sources`); сбой 1С (502) — текстом сервера
 * с «Повторить», а не пустым списком. Выбранный раньше склад, которого в
 * списке больше нет, показывается как есть — «нет свободного остатка», а не
 * пропадает (иначе select молча показал бы плейсхолдер).
 */
export function SampleSourceField({
  item,
  transfer,
  sources,
  sourcesLoading,
  sourcesError,
  onRetry,
  disabled,
  saving,
  error,
  onPick,
}: {
  item: ItemView;
  transfer: SampleTransferView | null;
  sources: StockSourcesView | undefined;
  sourcesLoading: boolean;
  sourcesError: unknown;
  onRetry: () => void;
  disabled: boolean;
  saving: boolean;
  error: string;
  onPick: (warehouseRef: string) => void;
}): React.ReactElement {
  const selected = item.samples_source_warehouse;

  if (item.samples_transferred) {
    return (
      <div className="space-y-0.5 text-sm" data-testid="okk-sample-source">
        <div>
          <span className="text-muted-foreground">Откуда взяли: </span>
          {selected?.name ?? transfer?.source_warehouse.name ?? "—"}
        </div>
        <div className="text-xs text-emerald-800">
          оформлено документом №{item.samples_transfer_number ?? transfer?.one_c_number ?? "—"}
          {transfer && !transfer.posted && (
            <span className="text-amber-800"> · не проведён</span>
          )}
        </div>
      </div>
    );
  }

  const options = sources?.sources ?? [];
  const selectedMissing =
    selected !== null && sources !== undefined && !options.some((s) => s.warehouse_ref === selected.ref);

  return (
    <div className="space-y-1" data-testid="okk-sample-source">
      <label className="block">
        <span className="mb-1 block text-xs text-muted-foreground">Откуда взяли</span>
        {sources ? (
          <select
            aria-label="Откуда взяли"
            value={selected?.ref ?? ""}
            disabled={disabled || saving || (options.length === 0 && !selected)}
            onChange={(e) => {
              if (e.target.value) onPick(e.target.value);
            }}
            className="h-10 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-60"
          >
            <option value="" disabled>
              — выберите склад —
            </option>
            {selectedMissing && (
              <option value={selected.ref}>{selected.name} — нет свободного остатка</option>
            )}
            {options.map((s) => (
              <option key={s.warehouse_ref} value={s.warehouse_ref}>
                {sourceOptionText(s.warehouse_name, s.free_qty, sources.unit_name)}
              </option>
            ))}
          </select>
        ) : (
          <div className="flex h-10 items-center rounded-md border bg-muted/40 px-2 text-sm text-muted-foreground">
            {selected ? selected.name : "склад не выбран"}
          </div>
        )}
      </label>
      {sourcesLoading && !sources && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Остатки из 1С…
        </div>
      )}
      {sourcesError !== null && sourcesError !== undefined && !sourcesLoading && (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-xs text-rose-700">
          <span>{apiErrorText(sourcesError, "Не удалось получить остатки из 1С")}</span>
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            Повторить
          </Button>
        </div>
      )}
      {sources && options.length === 0 && (
        <div className="text-xs text-amber-800">
          Свободного остатка позиции нет ни на одном складе организации
        </div>
      )}
      {error && (
        <div role="alert" className="text-xs text-rose-700">
          {error}
        </div>
      )}
    </div>
  );
}

/** Единица образцов «в архив» оформленной позиции — из строки её документа. */
export function transferUnit(
  transfer: SampleTransferView | null,
  itemId: string,
): string | null {
  return transfer?.lines.find((l) => l.item_id === itemId)?.unit_name ?? null;
}

/**
 * Блок «Образцы в архив» карточки проверки: документы 1С и кнопка
 * «Оформить». Отказы гейтов (409) и сбой проведения (502) — текстом сервера;
 * после отказа карточка перечитывается (502 значит «документ создан, но не
 * проведён» — он уже есть в списке и его надо показать).
 */
export function SamplesTransfersBlock({
  detail,
  onSaved,
  onChanged,
  busyItems,
}: {
  detail: InspectionDetail;
  onSaved: (detail: InspectionDetail) => void;
  onChanged: () => void;
  /** Позиции, у которых количество образцов набрано, но не сохранено (или идёт
   *  сохранение): пока они есть, оформлять нельзя — документ уйдёт со старым числом. */
  busyItems: string[];
}): React.ReactElement | null {
  const [banner, setBanner] = useState("");
  const run = useMutation({
    mutationFn: () => createSampleTransfers(detail.id),
    onMutate: () => setBanner(""),
    onSuccess: onSaved,
    onError: (e) => {
      setBanner(apiErrorText(e));
      onChanged();
    },
  });

  const transfers = detail.samples_transfers;
  const unposted = transfers.some((t) => !t.posted);
  const busy = busyItems.length > 0;
  if (!detail.samples_pending && transfers.length === 0) return null;

  const noSource = detail.items.filter(
    (i) =>
      !i.removed_from_delivery &&
      !i.samples_transferred &&
      i.samples_archive_qty > 0 &&
      i.samples_source_warehouse === null,
  );

  return (
    <Card className="space-y-2 p-3" data-testid="okk-samples-block">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <Archive className="size-4" />
        Образцы в архив
      </h2>
      <p className="text-xs text-muted-foreground">{SAMPLES_HINT}</p>
      <div className="text-sm">
        <span className="text-muted-foreground">Склад арбитража: </span>
        {detail.samples_target_warehouse ? (
          detail.samples_target_warehouse.name
        ) : (
          <span className="text-amber-800">не настроен (Настройки → Приёмка)</span>
        )}
      </div>

      {transfers.map((t) => (
        <div key={t.id} className="space-y-1 rounded-md border p-2 text-sm" data-testid="okk-sample-transfer">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">№{t.one_c_number || t.one_c_ref}</span>
            <span className="min-w-0 break-words">
              {t.source_warehouse.name} → {t.target_warehouse.name}
            </span>
            <StatusBadge tone={t.posted ? "ok" : "bad"}>
              {t.posted ? "проведён" : "не проведён"}
            </StatusBadge>
          </div>
          <div className="text-xs text-muted-foreground">
            {fmtDateTime(t.created_at)} · {authorName(t.created_by)}
          </div>
          <ul className="space-y-0.5 text-xs">
            {t.lines.map((l) => (
              <li key={l.item_id} className="flex flex-wrap gap-x-2">
                <span className="min-w-0 flex-1 break-words">
                  {l.nomenclature_name}
                  {l.characteristic_name ? ` · ${l.characteristic_name}` : ""}
                  {l.partia_number ? ` · партия ${l.partia_number}` : ""}
                </span>
                <span className="tabular-nums">
                  {ruNum.format(l.qty)} {l.unit_name}
                </span>
              </li>
            ))}
          </ul>
          {!t.posted && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={run.isPending || busy}
              onClick={() => run.mutate()}
            >
              {run.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              Провести повторно
            </Button>
          )}
        </div>
      ))}

      {busy && (
        <div className="text-xs text-amber-800" data-testid="okk-samples-busy">
          Количество образцов ещё не сохранено: {busyItems.join(", ")}
        </div>
      )}

      {detail.samples_pending && (
        <div className="space-y-1">
          <Button
            type="button"
            disabled={run.isPending || busy}
            onClick={() => run.mutate()}
            className="w-full sm:w-auto"
          >
            {run.isPending ? <Loader2 className="animate-spin" /> : <Archive />}
            Оформить образцы в архив
          </Button>
          {noSource.length > 0 && (
            <div className="text-xs text-amber-800">
              Не выбран склад-источник: {noSource.map((i) => i.nomenclature_name).join(", ")}
            </div>
          )}
          {unposted && (
            <div className="text-xs text-muted-foreground">
              Непроведённые документы проведутся вместе с оформлением.
            </div>
          )}
        </div>
      )}

      {banner && (
        <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
          {banner}
        </div>
      )}
    </Card>
  );
}
